// Sector-cascade drift readout (Phase C of Plans/sector_cascade_rebalance.md).
//
// Renders the snapshot `cascade_daily_drift_check` persists nightly (Phase A):
//   drift = ideal(today) − actual(today), per sector and per name.
// Both sides are recomputed as-of-today, so this captures BOTH market drift and
// signal change — there is no frozen inception target.
//
// READ-ONLY. It never triggers a recompute or a trade. While `auto_rebalance` is
// off (the default) the engine only records `would_rebalance` — nothing is staged.
import React, { useEffect, useState } from 'react';
import { tradingService } from '../../../services/tradingService';
import { useSortableRows, SortableTh } from '../../../components/tables/SortableTable';

// Drift tables are Object.entries → [key, {ideal, actual, drift}]. Shared columns for both.
const driftCols = (keyName) => [
  { key: keyName, type: 'string', get: (e) => e[0] },
  { key: 'ideal', type: 'number', get: (e) => e[1].ideal },
  { key: 'actual', type: 'number', get: (e) => e[1].actual },
  { key: 'drift', type: 'number', get: (e) => e[1].drift },
];
const SECTOR_DRIFT_COLS = driftCols('sector');
const NAME_DRIFT_COLS = driftCols('name');

const pct = (v, dp = 2) => (v == null || isNaN(v) ? '—' : `${(v * 100).toFixed(dp)}%`);
const signPct = (v, dp = 2) =>
  (v == null || isNaN(v) ? '—' : `${v >= 0 ? '+' : ''}${(v * 100).toFixed(dp)}%`);
const driftClass = (v) =>
  (v == null || isNaN(v) ? 'text-gray-400' : v > 0 ? 'text-emerald-500' : v < 0 ? 'text-red-500' : 'text-gray-400');
const ago = (iso) => {
  if (!iso) return '—';
  const m = (Date.now() - new Date(iso).getTime()) / 60000;
  if (m < 60) return `${Math.round(m)} min ago`;
  if (m < 1440) return `${(m / 60).toFixed(1)} h ago`;
  return `${(m / 1440).toFixed(1)} d ago`;
};

const Tile = ({ label, value, valueClass = 'text-gray-800 dark:text-gray-200', sub }) => (
  <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg p-3">
    <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">{label}</div>
    <div className={`text-lg font-semibold tabular-nums ${valueClass}`}>{value}</div>
    {sub && <div className="text-[10px] text-gray-400 mt-0.5">{sub}</div>}
  </div>
);

const RebalanceDriftPanel = ({ portfolioId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [showNames, setShowNames] = useState(false);

  useEffect(() => {
    if (!portfolioId) return;
    setLoading(true); setErr(null);
    tradingService.getPortfolioRebalanceStatus(portfolioId)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.error || 'Failed to load drift status'))
      .finally(() => setLoading(false));
  }, [portfolioId]);

  // Sort hooks must run every render (before any early return) — feed them from data?.status,
  // which is null-safe. Default order = backend's (by |drift|); clicking a header re-sorts.
  const _s = data?.status || {};
  const sectorSort = useSortableRows(Object.entries(_s.sector_drift || {}), SECTOR_DRIFT_COLS);
  const nameSort = useSortableRows(Object.entries(_s.name_drift || {}), NAME_DRIFT_COLS);

  if (loading && !data) return <div className="h-24 skeleton rounded-xl" />;
  if (err) return <div className="text-xs text-red-500">{err}</div>;
  if (!data) return null;

  // Cap-weight baskets have no cascade recipe — nothing to drift-check.
  if (!data.is_cascade) {
    return (
      <div className="text-[11px] text-gray-400 italic">
        Drift monitoring applies to sector-cascade baskets only (this one is cap-weighted).
      </div>
    );
  }

  const s = data.status || {};
  const v = s.verdict || {};
  const recipe = data.recipe || {};
  const sectors = sectorSort.sorted;                 // sortable (default = backend |drift| order)
  const nameBreaches = v.name_breaches || s.name_breaches || [];
  const nameBreachSet = new Set(nameBreaches);
  const sectorBreaches = new Set(v.sector_breaches || s.sector_breaches || []);
  const nameRows = nameSort.sorted;

  if (!data.has_snapshot) {
    return (
      <div className="rounded-md border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-[#161b22] px-3 py-2.5 text-[11px] text-gray-600 dark:text-gray-400">
        No drift snapshot yet — the nightly check runs once this basket has been live for a
        session. It will then show ideal-vs-actual weights per sector and per name.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* header */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-[11px] text-gray-500 dark:text-gray-400">
          Measured {ago(s.computed_at)}
          {s.total_value != null && ` · book $${Number(s.total_value).toLocaleString('en-US', { maximumFractionDigits: 0 })}`}
          {s.days_since_rebalance != null && ` · ${s.days_since_rebalance}d since last rebalance`}
        </div>
        <span className={`text-[10px] px-2 py-0.5 rounded-full border ${
          data.auto_rebalance
            ? 'border-amber-400/50 bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300'
            : 'border-gray-300 dark:border-gray-700 text-gray-500'
        }`}>
          {data.auto_rebalance ? 'AUTO-REBALANCE ON' : 'MEASURE-ONLY (no trades)'}
        </span>
      </div>

      {/* verdict */}
      <div className={`rounded-md border px-3 py-2 text-[11px] ${
        v.would_rebalance
          ? 'border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 text-amber-800 dark:text-amber-300'
          : 'border-emerald-300 dark:border-emerald-900 bg-emerald-50 dark:bg-emerald-950/20 text-emerald-700 dark:text-emerald-400'
      }`}>
        <span className="font-semibold">
          {v.would_rebalance ? 'WOULD REBALANCE' : 'Within bands'}
        </span>
        {(v.reasons || []).length > 0 && <span> — {(v.reasons || []).join('; ')}</span>}
        {v.cooldown_active && !v.would_rebalance && <span> · cooldown active</span>}
        {v.mandatory_due && <span> · mandatory cadence due</span>}
        {v.would_rebalance && !data.auto_rebalance && (
          <span className="block mt-0.5 text-[10px] opacity-80">
            Auto-rebalance is off — this is recorded only, nothing was staged.
          </span>
        )}
      </div>

      {/* tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile label="Max sector drift" value={pct(s.max_sector_drift)}
              valueClass={sectorBreaches.size ? 'text-amber-500' : 'text-gray-800 dark:text-gray-200'}
              sub={`band max(${((recipe.sector_band_rel ?? 0) * 100).toFixed(0)}% rel, ${((recipe.sector_band_floor_pp ?? 0) * 100).toFixed(1)}pp)`} />
        <Tile label="Max name drift" value={pct(s.max_name_drift)}
              valueClass={nameBreaches.length ? 'text-amber-500' : 'text-gray-800 dark:text-gray-200'}
              sub={`band ${((recipe.name_band_pp ?? 0) * 100).toFixed(1)}pp`} />
        <Tile label="Sectors breached" value={sectorBreaches.size}
              valueClass={sectorBreaches.size ? 'text-amber-500' : 'text-gray-400'} />
        <Tile label="Names breached" value={nameBreaches.length}
              valueClass={nameBreaches.length ? 'text-amber-500' : 'text-gray-400'} />
      </div>

      {/* per-sector ideal vs actual */}
      {sectors.length > 0 && (
        <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-3 overflow-x-auto scrollbar-thin">
          <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-2">
            Sector drift · ideal (today) vs actual
          </div>
          <table className="w-full text-[11px] tabular-nums">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">
                <SortableTh label="Sector" colKey="sector" align="left" className="py-1 pr-3" toggle={sectorSort.toggle} indicator={sectorSort.indicator} />
                <SortableTh label="Ideal" colKey="ideal" className="py-1 px-2" toggle={sectorSort.toggle} indicator={sectorSort.indicator} />
                <SortableTh label="Actual" colKey="actual" className="py-1 px-2" toggle={sectorSort.toggle} indicator={sectorSort.indicator} />
                <SortableTh label="Drift" colKey="drift" className="py-1 pl-2" toggle={sectorSort.toggle} indicator={sectorSort.indicator} />
              </tr>
            </thead>
            <tbody>
              {sectors.map(([sec, d]) => {
                const breached = sectorBreaches.has(sec);
                return (
                  <tr key={sec} className={`border-t border-gray-100 dark:border-gray-800 ${breached ? 'bg-amber-50/60 dark:bg-amber-950/20' : ''}`}>
                    <td className="text-left py-1.5 pr-3 text-gray-700 dark:text-gray-300">
                      {sec}{breached && <span className="ml-1 text-amber-500" title="over band">▲</span>}
                    </td>
                    <td className="text-right py-1.5 px-2 text-gray-500">{pct(d.ideal)}</td>
                    <td className="text-right py-1.5 px-2 text-gray-500">{pct(d.actual)}</td>
                    <td className={`text-right py-1.5 pl-2 font-medium ${driftClass(d.drift)}`}>{signPct(d.drift)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="text-[10px] text-gray-400 mt-2">
            Drift = ideal − actual. Positive = underweight vs today's ideal (needs buying).
          </div>
        </div>
      )}

      {/* name breaches */}
      {nameBreaches.length > 0 && (
        <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-3">
          <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-1.5">
            Names over the {((recipe.name_band_pp ?? 0) * 100).toFixed(1)}pp band · {nameBreaches.length}
          </div>
          <div className="flex flex-wrap gap-1">
            {nameBreaches.map((n) => (
              <span key={n} className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/30">
                {n}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* per-name ideal vs actual (top-N by |drift|, collapsible) */}
      {nameRows.length > 0 ? (
        <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden">
          <button
            onClick={() => setShowNames((x) => !x)}
            className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-white/5">
            <span className="text-[10px] uppercase tracking-wider text-gray-400">
              Per-name drift · ideal (today) vs actual
              {s.name_count > nameRows.length
                ? ` · top ${nameRows.length} of ${s.name_count} by drift`
                : ` · ${nameRows.length} names`}
            </span>
            <span className="text-gray-400 text-xs">{showNames ? '▲' : '▼'}</span>
          </button>
          {showNames && (
            <div className="px-3 pb-3 overflow-x-auto scrollbar-thin">
              <table className="w-full text-[11px] tabular-nums">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">
                    <SortableTh label="Name" colKey="name" align="left" className="py-1 pr-3" toggle={nameSort.toggle} indicator={nameSort.indicator} />
                    <SortableTh label="Ideal" colKey="ideal" className="py-1 px-2" toggle={nameSort.toggle} indicator={nameSort.indicator} />
                    <SortableTh label="Actual" colKey="actual" className="py-1 px-2" toggle={nameSort.toggle} indicator={nameSort.indicator} />
                    <SortableTh label="Drift" colKey="drift" className="py-1 pl-2" toggle={nameSort.toggle} indicator={nameSort.indicator} />
                  </tr>
                </thead>
                <tbody>
                  {nameRows.map(([nm, d]) => {
                    const breached = nameBreachSet.has(nm);
                    return (
                      <tr key={nm} className={`border-t border-gray-100 dark:border-gray-800 ${breached ? 'bg-amber-50/60 dark:bg-amber-950/20' : ''}`}>
                        <td className="text-left py-1.5 pr-3 font-mono text-gray-700 dark:text-gray-300">
                          {nm}{breached && <span className="ml-1 text-amber-500" title="over band">▲</span>}
                        </td>
                        <td className="text-right py-1.5 px-2 text-gray-500">{pct(d.ideal)}</td>
                        <td className="text-right py-1.5 px-2 text-gray-500">{pct(d.actual)}</td>
                        <td className={`text-right py-1.5 pl-2 font-medium ${driftClass(d.drift)}`}>{signPct(d.drift)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="text-[10px] text-gray-400 mt-2">
                Drift = ideal − actual. Positive = underweight vs today's ideal (needs buying).
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="text-[10px] text-gray-400 italic">
          Per-name drift table populates on the next nightly check.
        </div>
      )}

      {/* rebalance activity (Phase B): preview / staged / blocked events */}
      {(data.rebalance_events || []).length > 0 && (
        <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-3">
          <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-2">
            Rebalance activity
          </div>
          <div className="space-y-1.5">
            {(data.rebalance_events || []).map((e, i) => {
              const badge = e.status === 'staged'
                ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30'
                : e.status === 'blocked'
                ? 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/30'
                : 'bg-gray-500/10 text-gray-500 border-gray-400/40';
              return (
                <div key={i} className="flex items-center justify-between gap-2 text-[11px] border-t border-gray-100 dark:border-gray-800 pt-1.5 first:border-t-0 first:pt-0 flex-wrap">
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500 tabular-nums">{e.as_of_date}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${badge}`}>
                      {e.status}{e.dry_run ? ' · dry' : ''}
                    </span>
                    {e.reason && <span className="text-[10px] text-red-500">{e.reason}</span>}
                  </div>
                  <div className="flex items-center gap-3 text-gray-500 tabular-nums">
                    {e.turnover != null && <span>turnover {pct(e.turnover)}</span>}
                    {e.n_trades != null && <span>{e.n_trades} trades</span>}
                    {e.buys_usd != null && (
                      <span className="text-emerald-500">+${Number(e.buys_usd).toLocaleString('en-US', { maximumFractionDigits: 0 })}</span>
                    )}
                    {e.sells_usd != null && (
                      <span className="text-red-500">−${Number(e.sells_usd).toLocaleString('en-US', { maximumFractionDigits: 0 })}</span>
                    )}
                    {/* 2026-10-13: the book's $ net split at the half-share line — whole-share legs
                        reach the broker, sub-half-share crumbs cross into the fractional reserve. */}
                    {e.net_usd != null && (
                      <span className={Number(e.net_usd) >= 0 ? 'text-emerald-500' : 'text-red-500'}>
                        net {Number(e.net_usd) >= 0 ? '+' : '−'}${Math.abs(Number(e.net_usd)).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                        {e.crumb_net_usd != null && (
                          <span className="text-gray-400"> (crumbs {Number(e.crumb_net_usd) >= 0 ? '+' : '−'}${Math.abs(Number(e.crumb_net_usd)).toLocaleString('en-US', { maximumFractionDigits: 0 })})</span>
                        )}
                      </span>
                    )}
                    {e.staged_tradeable != null && (
                      <span className="text-gray-400">{e.staged_tradeable} orders · {e.staged_placeholder ?? 0} crumbs</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="text-[10px] text-gray-400 mt-2">
            {data.auto_rebalance
              ? 'Auto-rebalance ON — staged nightly on a breach.'
              : 'Measure-only — these are dry-run previews; nothing was staged.'}
          </div>
          {/* capital basis (2026-10-13): what the next rebalance sizes on — the sleeves' equity
              (holdings + residual cash), not the holdings total. The ratio is the over-deployment
              drift cannot show (weights sum to 1 on both sides). */}
          {data.status && data.status.capital_basis != null && (
            <div className="text-[10px] text-gray-400 mt-1 tabular-nums">
              Capital basis ${Number(data.status.capital_basis).toLocaleString('en-US', { maximumFractionDigits: 0 })}
              {' '}({data.status.basis_source})
              {data.status.holdings_value != null && (
                <> · holdings ${Number(data.status.holdings_value).toLocaleString('en-US', { maximumFractionDigits: 0 })}</>
              )}
              {data.status.residual_cash != null && (
                <> · residual cash <span className={Number(data.status.residual_cash) >= 0 ? 'text-emerald-500' : 'text-red-500'}>
                  {Number(data.status.residual_cash) >= 0 ? '+' : '−'}${Math.abs(Number(data.status.residual_cash)).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                </span></>
              )}
              {data.status.deployment_ratio != null && (
                <> · deployed <span className={Math.abs(Number(data.status.deployment_ratio) - 1) <= 0.02 ? 'text-emerald-500' : 'text-red-500'}>
                  {(Number(data.status.deployment_ratio) * 100).toFixed(1)}%
                </span> of capital</>
              )}
              {data.status.basis_stale_sleeves > 0 && (
                <> · {data.status.basis_stale_sleeves} sleeve residual(s) older than {data.status.basis_as_of}</>
              )}
            </div>
          )}
        </div>
      )}

      {/* the bands being applied */}
      <div className="text-[10px] text-gray-400">
        Bands: name {((recipe.name_band_pp ?? 0) * 100).toFixed(1)}pp ·
        sector max({((recipe.sector_band_rel ?? 0) * 100).toFixed(0)}% rel, {((recipe.sector_band_floor_pp ?? 0) * 100).toFixed(1)}pp) ·
        mandatory {recipe.mandatory_cadence || '—'} · cooldown {recipe.cooldown_days ?? '—'}d
        {data.last_rebalance_at ? ` · last rebalance ${new Date(data.last_rebalance_at).toLocaleDateString()}` : ' · never rebalanced'}
      </div>
    </div>
  );
};

export default RebalanceDriftPanel;
